import { useEffect, useState } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import AudioPlayer from '@/trio-ui/AudioPlayer';
import AudioRecorderComponent from './AudioRecorder.component';

export type AudioRecorderProps = {
  onExternalSave?: (event: any) => void;
  defaultUrl?: string;
  onDeleteAudio?: () => void;
};

export default function AudioRecorder({
  defaultUrl = '',
  onExternalSave = () => {},
  onDeleteAudio = () => {},
}: AudioRecorderProps): JSX.Element {
  const { startRecording, stopRecording, recordingBlob, isRecording, recordingTime } =
    useAudioRecorder();
  const [microphonePermission, setMicrophonePermission] = useState({
    checked: false,
    enabled: false,
  });

  const [audioUrl, setAudioUrl] = useState(defaultUrl);
  const [hasEnded, setHasEnded] = useState(!!defaultUrl);

  useEffect(() => {
    if (recordingBlob && !isRecording) {
      const url = URL.createObjectURL(recordingBlob);
      setAudioUrl(url);
      setHasEnded(true);
      onExternalSave({ url, recordingBlob });
    }
  }, [isRecording, onExternalSave, recordingBlob, recordingTime]);

  const onDelete = (): void => {
    setAudioUrl('');
    setHasEnded(false);
    onDeleteAudio();
  };

  const onStartRecording = async (): Promise<void> => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermission({
        checked: true,
        enabled: true,
      });
      startRecording();
    } catch {
      setMicrophonePermission({
        checked: true,
        enabled: false,
      });
    }
  };

  const onConfirmMicrophoneAlert = (): void => {
    setMicrophonePermission({
      checked: false,
      enabled: false,
    });
  };

  const micWithPermission = microphonePermission.checked && !microphonePermission.enabled;

  if (hasEnded) {
    return <AudioPlayer audioUrl={audioUrl} onDelete={onDelete} />;
  }

  return (
    <AudioRecorderComponent
      onConfirmMicrophoneAlert={onConfirmMicrophoneAlert}
      microphoneWithoutPermission={micWithPermission}
      onStartRecording={onStartRecording}
      onStopRecording={stopRecording}
      recordingTime={recordingTime}
      isRecording={isRecording}
    />
  );
}
